import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import ResourceSelect from "../../../../../../../../components/shared/inputs/ResourceSelect";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { useDateFilters } from "../../../../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "../../../../../../../../utils/helpers/settings";
import { unassignedShiftsResource } from "../../../../../helpers/data/unassignedShiftsResource";
import { useUser } from "../../../../../../../../utils/hooks/user";

export type QualificationField = "skills" | "licenses" | "certifications";

const SETTINGS_RESOURCE_PATHS: Record<QualificationField, string> = {
    skills: "/skill",
    licenses: "/license",
    certifications: "/certification",
};

const getUserQualificationIds = (user: any, field: QualificationField): string[] => {
    const items: any[] = user?.[field] ?? [];
    if (field === "skills") return items.map((item) => item?.id ?? item).filter(Boolean);
    return items
        .filter((item) => item?.status !== "expired")
        .map((item) => item?.type?.id ?? item?.type)
        .filter(Boolean);
};

type QualificationSelectProps = {
    field: QualificationField;
    value?: any[];
    onChange?: (value: any[]) => void;
    disabled?: boolean;
    className?: string;
};

function QualificationSelect({ field, value, onChange, disabled, className }: QualificationSelectProps) {
    const { t } = useTranslation();
    const { selectedCellOrEvent, type, allResources } = useScheduleContext();
    const { mode } = useDateFilters();
    const { control } = useFormContext();
    const users = useWatch({ control, name: "users" });
    const loggedUser = useUser();

    const isUnassigned = selectedCellOrEvent?.isUnassignedShift ||
        selectedCellOrEvent?.resourceId === unassignedShiftsResource.id;

    // Only the qualifications every selected user holds
    const ids: string[] = useMemo(() => {
        // The event/cell only carries the user's id (or a trimmed copy), the qualifications live on the loaded resource
        const toResource = (user: any) => {
            const id = typeof user === "object" ? user?.id : user;
            return allResources?.find((resource: any) => resource?.id === id)
                || (typeof user === "object" ? user : undefined);
        };
        const user = selectedCellOrEvent?.user
            ?? selectedCellOrEvent?.resourceId
            ?? (type === "home" ? loggedUser : undefined);
        const subjects: any[] = (mode === "month" ? (users ?? []) : [user])
            .map(toResource)
            .filter(Boolean);
        const [first = [], ...rest] = subjects.map((subject) => getUserQualificationIds(subject, field));
        return Array.from(new Set(first)).filter((id) => rest.every((list) => list.includes(id)));
    }, [selectedCellOrEvent?.user, selectedCellOrEvent?.resourceId, allResources, type, loggedUser, mode, users, field]);

    // Scope the options to the user's qualifications; open shifts have no user, so they get every one
    const params = useMemo(() => isUnassigned ? undefined : {
        "filter[id][value]": ids,
        "filter[id][method]": "exact",
    }, [isUnassigned, ids]);

    return (
        <ResourceSelect
            mode="multiple"
            resourcePath={SETTINGS_RESOURCE_PATHS[field]}
            renderLabel={renderSettingsResourceLabel}
            getSearchFilter={getSettingsResourceSearchFilters}
            params={params}
            enabled={isUnassigned || ids.length > 0}
            placeholder={t(field)}
            disabled={disabled}
            value={value}
            onChange={onChange as (value?: any) => void}
            className={className}
        />
    );
}

export default QualificationSelect;
